import { ErrorComponent } from "@refinedev/antd";
import { Authenticated, CanAccess, useIsAuthenticated } from "@refinedev/core";
import { CatchAllNavigate } from "@refinedev/react-router-v6";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ThemedLayout } from "./layouts/ThemedLayout";
import { Title } from "./components/header/Title";
import { Header } from "./components/header";
import { Footer } from "./components/footer";
import { HomePage } from "./pages/home";
import { InstancePage } from "./pages/instance";
import { TenantListPage } from "./pages/tenants";
import { ShowInstanceDetailPage } from "./components/instance/ShowDetail";
import { LoginPage } from "./pages/auth/Login";
import { RegisterPage } from "./pages/auth/Register";
import { ForgotPasswordPage } from "./pages/auth/ForgotPassword";
import { useEffect, useState } from "react";
import { initAxios } from "./apis/axios";
import { useAuthContext } from "./providers/auth-provider";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "antd-style";
import { DevGuide } from "./pages/dev";
import { EKSPage } from "./pages/eks";
import { CostPage } from "./pages/cost";
import { ShowEks } from "./pages/eks/ShowEks";
import { PoolPage } from "./pages/pools";
import { LaunchInstance } from "./pages/instance/LaunchInstance";
import { LaunchPool } from "./pages/pools/LaunchPool";
import { LaunchNodegroup } from "./pages/nodegroups/LaunchNodegroup";
import { CreateNodeGroupPage } from "./pages/nodegroups/creation";
import { NodegroupPage } from "./pages/nodegroups";
import { Sider } from "./components/sider";
import { ActionPage } from "./pages/instance/ActionPage";
import { ShowInstance } from "./pages/instance/ShowInstance";
import { CreatePoolPage } from "./pages/pools/creation";
import { ShowPool } from "./pages/pools/show";
import { PoolEditor } from "./pages/pools/editor";
import { ResourceProvider } from "./layouts/ResourceContext";
import { NodegroupEditor } from "./pages/nodegroups/editor";
import { TenantIndexPage } from "./pages/home/tenant-index";
import { UsersListPage } from "./pages/users";
import { K8SNodesPage, K8SContainer } from "./pages/k8s";
import { K8SIndexPage } from "./pages/home/k8s-index";
import { CreateTenantPage } from "./pages/tenants/creation";
import { EditTenantPage } from "./pages/tenants/editor";
import { UserCreation } from "./pages/users/creation";
import { UserEditor } from "./pages/users/editor";
import { VitriaIcon } from "./components/VitriaIcon";
import { ProfileOutlined, ScheduleOutlined, SettingOutlined, UserOutlined } from "@ant-design/icons";
import { SSOLoginPage } from "./pages/auth/SSOLogin";
import { EntryPage } from "./pages/home/entry";
import { BarChartOutlined } from "@ant-design/icons";
import { AddonsPage } from "./pages/addons";
import { ShowAddonPage } from "./pages/addons/show";
import { VersionPage } from "./pages/settings/versions";
import { CreateVersionPage } from "./pages/settings/versions/creation";
import { VersionActionPage } from "./pages/settings/versions/VersionAction";
import { AddonRoute } from "./pages/addons/AddonRoute";
import { AddonCreator } from "./pages/addons/creation";
import { ProfilePage } from "./pages/settings/profiles";
import { CreateProfilePage } from "./pages/settings/profiles/creation";
import { ProfileActionPage } from "./pages/settings/profiles/ProfileAction";
import { ShowVersion } from "./pages/settings/versions/show";
import { SubscriptionPage } from "./pages/subscription";

export const TENANT_RESOURCES = (t) => [
  {
    name: "instances",
    list: "/instances",
    create: "/instances/create",
    show: "/instances/show/:id",
    edit: "/instances/:action/:id",
    meta: {
      icon: <VitriaIcon type="vicon-instance" />,
      label: t("common:instances.menu.label"),
    },
  },
  {
    name: "subscription",
    list: "/subscription",
    meta: {
      icon: <ScheduleOutlined />,
      label: t("subscription.menu.label", "Subscription"),
    },
  },
  {
    name: "cost",
    list: "/cost",
    meta: {
      icon: <BarChartOutlined />,
      label: t("cost.menu.label", "Cost Explorer"),
    },
  },
  {
    name: "users",
    list: "/users",
    create: "/users/create",
    show: "/users/show/:id",
    edit: "/users/:action/:id",
    meta: {
      icon: <UserOutlined />,
      label: t("users.menu.label"),
    },
  },
];

export const TENANT_USER_RESOURCES = (t) => [
  {
    name: "instances",
    list: "/instances",
    create: "/instances/create",
    show: "/instances/show/:id",
    edit: "/instances/:action/:id",
    meta: {
      icon: <VitriaIcon type="vicon-instance" />,
      label: t("common:instances.menu.label"),
    },
  }
];

export const K8S_RESOURCES = (t) => [
  {
    name: "nodes",
    list: "/k8s/:id/nodes",
    meta: {
      icon: <VitriaIcon type="vicon-instance" />,
      label: "Nodes",
    },
  },
  {
    name: "workloads",
    list: "/k8s/:id/workloads",
    meta: {
      icon: <VitriaIcon type="vicon-instance" />,
      label: "Workloads",
    },
  },
  {
    name: "pods",
    list: "/k8s/:id/workloads/pods",
    meta: { parent: "workloads", label: "Pods" },
  },
  {
    name: "ssets",
    list: "/k8s/:id/workloads/ssets",
    meta: { parent: "workloads", label: "Stateful Sets" },
  },
  {
    name: "config",
    list: "/k8s/:id/config",
    meta: {
      icon: <VitriaIcon type="vicon-instance" />,
      label: "Config",
    },
  },
];

export const OTHER_RESOURCES = (t) => [
  {
    name: "tenants",
    list: "/tenants",
    show: "/tenants/show/:id",
    create: "/tenants/create",
    edit: "/tenants/:action/:id",
    meta: {
      icon: <VitriaIcon type="vicon-tenant" />,
      label: t("tenants.menu.label", "Tenants"),
    },
  },
  {
    name: "users",
    list: "/users",
    create: "/users/create",
    show: "/users/show/:id",
    edit: "/users/:action/:id",
    meta: {
      icon: <UserOutlined />,
      label: t("users.menu.admin"),
    },
  },
  {
    name: "instances",
    list: "/instances",
    create: "/instances/create",
    show: "/instances/show/:id",
    edit: "/instances/:action/:id",
    meta: {
      icon: <VitriaIcon type="vicon-instance" />,
      label: t("common:instances.menu.label"),
    },
  },
  {
    name: "nodegroups",
    list: "/nodegroups",
    create: "/nodegroups/create",
    edit: "/nodegroups/:action/:id",
    meta: {
      icon: <VitriaIcon type="vicon-nodeGroup" />,
      label: t("nodegroups.menu.label", "Nodegroups"),
    },
  },
  {
    name: "pools",
    list: "/pools",
    show: "/pools/show/:id",
    create: "/pools/create",
    edit: "/pools/:action/:id",
    meta: {
      icon: <VitriaIcon type="vicon-pool" />,
      label: t("pools.menu.label", "Pools"),
    },
  },
  {
    name: "eks",
    list: "/eks",
    show: "/eks/show/:id",
    meta: {
      icon: <VitriaIcon type="vicon-eks" />,
      label: t("eks.menu.label", "EKS"),
    },
  },
  {
    name: "cost",
    list: "/cost",
    meta: {
      icon: <BarChartOutlined />,
      label: t("cost.menu.label", "Cost Explorer"),
    },
  },
  {
    name: "settings",
    list: "/settings",
    meta: {
      icon: <SettingOutlined />,
      label: t("settings.menu.label", "Settings"),
    },
  },
  {
    name: "profileSettings",
    list: "/settings/profiles",
    show: "/settings/profiles/show/:id",
    create: "/settings/profiles/create",
    edit: "/settings/profiles/:action/:id",
    meta: { parent: "settings", label: t("settings.profiles.menu.label", "Instance Profiles")},
  },
  {
    name: "versionSettings",
    list: "/settings/versions",
    show: "/settings/versions/show/:id",
    create: "/settings/versions/create",
    edit: "/settings/versions/:action/:id",
    meta: { parent: "settings", label: t("settings.versions.menu.label", "AIOps Versions") },
  },
  {
    name: "addonSettings",
    list: "/settings/addons",
    show: "/settings/addons/show/:id",
    create: "/settings/addons/create",
    edit: "/settings/addons/:action/:id",
    meta: { parent: "settings", label: t("settings.addons.menu.label", "Add-ons") },
  }

];

const TENANT_ADMIN_ROUTES = [
  <Route path="instances">
    <Route index element={<InstancePage />} />
    <Route path="show/:id" element={<ShowInstance />} />
    <Route path="create" element={<LaunchInstance />} />
    <Route path=":action/:id" element={<ActionPage goto="instances" />} />
  </Route>,
  <Route path="subscription">
    <Route index element={<SubscriptionPage />} />
  </Route> ,
  <Route path="cost">
    <Route index element={<CostPage />} />
  </Route> ,
  <Route path="users">
    <Route index element={<UsersListPage />} />
    <Route path="show/:id" element={<UsersListPage />} />
    <Route path="create" element={<UserCreation />} />
    <Route path=":action/:id" element={<UserEditor />} />
  </Route>,
];

const K8S_ROUTES = [
  <Route path="nodes">
    <Route index element={<K8SNodesPage />} />
  </Route>,
  <Route path="workloads/pods">
    <Route
      index
      element={
        <div>
          <b>Workloads Pods</b>
        </div>
      }
    />
  </Route>,
  <Route path="workloads/ssets">
    <Route
      index
      element={
        <div>
          <b>Workloads Stateful Sets</b>
        </div>
      }
    />
  </Route>,
  <Route path="config">
    <Route
      index
      element={
        <div>
          <b>Config</b>
        </div>
      }
    />
  </Route>,
];

export const RoutesConfig = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authProvider } = useAuthContext();
  const [user, setUser] = useState();
  const { t } = useTranslation();
  const { data: authenticatedInfo } = useIsAuthenticated();
  // console.log("isAuthenticated", authenticatedInfo);

  useEffect(() => {
    initAxios({
      unauthorizedHandler: (error, url) => {
        if (window.location.pathname === "/login" || window.location.pathname === "/login-sso" || window.location.pathname === "/register") {
          return;
        }
        authProvider.logout();
        navigate("/login");
        window.location.reload();
      },
      serviceErrorHandler: (err) => {
        return Promise.reject({
          message: t("errors.service_error"),
        });
      },
    });
    authProvider.getIdentity().then((user) => setUser(user));
  }, []);

  useEffect(() => {
    if (authenticatedInfo?.authenticated) {
      authProvider.getIdentity().then((user) => setUser(user));
    }
  }, [authenticatedInfo]);

  // console.log("user", user);

  return (
    <Routes>
      <Route
        element={
          <Authenticated key="authenticated-routes" fallback={<CatchAllNavigate to="/login" />}>
            <ThemedLayout Sider={Sider} Title={() => <Title />} Header={() => <Header />} Footer={() => <Footer />}>
              <CanAccess>
                <ResourceProvider>
                  <Outlet />
                </ResourceProvider>
              </CanAccess>
            </ThemedLayout>
          </Authenticated>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="eks">
          <Route index element={<EKSPage />} />
          <Route path="show/:id" element={<ShowEks />} />
        </Route>       
        <Route path="nodegroups">
          <Route index element={<NodegroupPage />} />
          <Route path="create" element={<LaunchNodegroup />} />
          <Route path=":action/:id" element={<NodegroupEditor />} />
        </Route>
        <Route path="pools">
          <Route index element={<PoolPage />} />
          <Route path="show/:id" element={<ShowPool />} />
          <Route path="create" element={<LaunchPool />} />
          <Route path=":action/:id" element={<PoolEditor />} />
        </Route>
        <Route path="settings">
          <Route index element={<div><a href="/settings/versions">AIOps Version</a></div>} />
        </Route>
        <Route path="settings/profiles">
          <Route
            index
            element={
              <ProfilePage />
            }
            />
            <Route path="show/:id" element={<div><b>Show Profile</b></div>} />
            <Route path="create" element={<CreateProfilePage />} />
            <Route path=":action/:id" element={<ProfileActionPage goto="settings/profiles" />} />
        </Route>
        <Route path="settings/versions">
          <Route
            index
            element={
              <VersionPage />
            }
          />
          <Route path="show/:id" element={<ShowVersion />} />
          <Route path="create" element={<CreateVersionPage />} />
          <Route path=":action/:id" element={<VersionActionPage goto="settings/versions" />} />
        </Route>
        <Route path="settings/addons">
          <Route index element={<AddonsPage />} />
          <Route path="show/:id" element={<ShowAddonPage/>}/>
          <Route path="create" element={<AddonCreator/> } />
          <Route path=":action/:id" element={<AddonRoute/>}/>
        </Route>
        {...TENANT_ADMIN_ROUTES}

        <Route path="tenants">
          <Route index element={<TenantListPage />} />
          <Route path="create" element={<CreateTenantPage />} />
          <Route path=":action/:id" element={<EditTenantPage/>}/>
        </Route>
        <Route path="t/:tenantName">
          <Route index element={<TenantIndexPage />} />
          {...TENANT_ADMIN_ROUTES}
        </Route>
        <Route
          path="k8s/:id"
          element={
            <K8SContainer key="K8SContainer">
              <Outlet />
            </K8SContainer>
          }
        >
          <Route index element={<K8SIndexPage to="nodes" />} />
          {...K8S_ROUTES}
        </Route>
        <Route path="dev" element={<DevGuide />} />
      </Route>
      <Route
        element={
          <ThemeProvider themeMode="light">
            <Authenticated key="auth-pages" fallback={<Outlet />}>
              {/* <Navigate to="/" /> */}
              <EntryPage />
            </Authenticated>
          </ThemeProvider>
        }
      >
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/login-sso" element={<SSOLoginPage />} />
      </Route>

      <Route path="*" element={<ErrorComponent />} />
    </Routes>
  );
};
